"use client";

import { ListingFilters } from "@/lib/fetch/listing";
import {
  getAllLocationCityOptions,
  getAllProvinceOptions,
  getLocationDisplayName,
} from "@/lib/data/location/data";
import { X } from "lucide-react";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import React, { useEffect, useMemo, useRef } from "react";

type Props = {
  filters: ListingFilters;
  setFilters: (filters: ListingFilters) => void;
  onReset: () => void;
};

type Chip = {
  id: string;
  label: string;
  /** Fields to apply when the chip is removed; only its own criterion. */
  reset: Partial<ListingFilters>;
};

/**
 * One removable chip per active criterion, built from the same filter state
 * the panel edits. Ranges collapse into a single chip.
 */
const SearchActiveFilters: React.FC<Props> = ({
  filters,
  setFilters,
  onReset,
}) => {
  const t = useTranslations("search");
  const tL = useTranslations("listing");
  const df = useFormatter();
  const locale = useLocale();
  const cities = useMemo(() => getAllLocationCityOptions(locale), [locale]);
  const provinces = useMemo(() => getAllProvinceOptions(locale), [locale]);
  const listRef = useRef<HTMLUListElement>(null);
  const focusIndex = useRef<number | null>(null);

  // A removed chip unmounts its own button, so hand focus to whatever now
  // sits in its place (the next chip, or Clear all).
  useEffect(() => {
    if (focusIndex.current === null) return;
    const buttons = listRef.current?.querySelectorAll("button");
    if (buttons?.length) {
      buttons[Math.min(focusIndex.current, buttons.length - 1)].focus();
    }
    focusIndex.current = null;
  });

  const named = (name: string, value: string) =>
    t("active-filters.label-value", { name, value });

  const range = (
    min: number | undefined,
    max: number | undefined,
    format: (value: number) => string,
  ) => {
    if (min && max)
      return t("active-filters.between", { min: format(min), max: format(max) });
    if (min) return t("active-filters.from", { value: format(min) });
    return t("active-filters.to", { value: format(max!) });
  };

  const euro = (value: number) =>
    df.number(value, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    });
  const count = (value: number) => df.number(value);
  const plain = (value: number) => String(value);
  const unit = (suffix: string) => (value: number) =>
    `${df.number(value)} ${suffix}`;

  const chips: Chip[] = [];

  if (filters.search) {
    chips.push({
      id: "search",
      label: named(t("search"), `“${filters.search}”`),
      reset: { search: undefined },
    });
  }

  const place = (
    key: "province" | "city" | "borough" | "neighborhood" | "street",
    name: string,
    label: string | undefined,
  ) => {
    const value = filters[key];
    if (!value || value === "ANY") return;
    chips.push({
      id: key,
      label: named(name, label ?? getLocationDisplayName(value, locale)),
      reset: { [key]: undefined },
    });
  };
  place(
    "province",
    t("location.province.name"),
    provinces.find((option) => option.value === filters.province)?.label,
  );
  place(
    "city",
    t("location.city.name"),
    cities.find((option) => option.value === filters.city)?.label,
  );
  place("borough", t("active-filters.borough"), undefined);
  place("neighborhood", t("active-filters.neighborhood"), undefined);
  place("street", t("active-filters.street"), undefined);

  filters.type?.forEach((type) => {
    chips.push({
      id: `type-${type}`,
      label: tL(`attributes.PROPERTY_TYPE.${type}`),
      reset: {
        type: filters.type!.filter((value) => value !== type).length
          ? filters.type!.filter((value) => value !== type)
          : undefined,
      },
    });
  });

  const ranges: Array<{
    min: keyof ListingFilters;
    max: keyof ListingFilters;
    name: string;
    format: (value: number) => string;
  }> = [
    { min: "priceMin", max: "priceMax", name: tL("attribute_title.PRICE"), format: euro },
    { min: "areaMin", max: "areaMax", name: tL("attribute_title.SIZE"), format: unit("m²") },
    { min: "bedroomsMin", max: "bedroomsMax", name: tL("attribute_title.BEDROOMS"), format: count },
    { min: "bathroomsMin", max: "bathroomsMax", name: tL("attribute_title.BATHROOMS"), format: count },
    { min: "volumeMin", max: "volumeMax", name: tL("attribute_title.VOLUME"), format: unit("m³") },
    { min: "floorsMin", max: "floorsMax", name: tL("attribute_title.NUMBER_OF_FLOORS"), format: count },
    { min: "gardenAreaMin", max: "gardenAreaMax", name: tL("attribute_title.GARDEN_SIZE"), format: unit("m²") },
    { min: "yearMin", max: "yearMax", name: tL("attribute_title.CONSTRUCTION_YEAR"), format: plain },
  ];
  ranges.forEach(({ min, max, name, format }) => {
    const minValue = filters[min] as number | undefined;
    const maxValue = filters[max] as number | undefined;
    if (!minValue && !maxValue) return;
    chips.push({
      id: min,
      label: named(name, range(minValue, maxValue, format)),
      reset: { [min]: undefined, [max]: undefined },
    });
  });

  const options: Array<{
    key: "furnished" | "condition" | "parkingType" | "petsAllowed" | "tenantType";
    attribute:
      | "FURNISHING_TYPE"
      | "CONDITION"
      | "PARKING_TYPE"
      | "PETS_ALLOWED"
      | "TENANT_TYPE";
  }> = [
    { key: "furnished", attribute: "FURNISHING_TYPE" },
    { key: "condition", attribute: "CONDITION" },
    { key: "parkingType", attribute: "PARKING_TYPE" },
    { key: "petsAllowed", attribute: "PETS_ALLOWED" },
    { key: "tenantType", attribute: "TENANT_TYPE" },
  ];
  options.forEach(({ key, attribute }) => {
    const value = filters[key];
    if (!value || value === "ANY") return;
    chips.push({
      id: key,
      label: named(
        tL(`attribute_title.${attribute}`),
        tL(`attributes.${attribute}.${value}` as never),
      ),
      reset: { [key]: key === "tenantType" ? undefined : "ANY" },
    });
  });

  filters.energyLabel?.forEach((label) => {
    chips.push({
      id: `energy-${label}`,
      label: tL("energy-label-text", { label }),
      reset: {
        energyLabel: filters.energyLabel!.filter((value) => value !== label)
          .length
          ? filters.energyLabel!.filter((value) => value !== label)
          : undefined,
      },
    });
  });

  filters.buildingFacilities?.forEach((facility) => {
    chips.push({
      id: `facility-${facility}`,
      label: tL(`attributes.BUILDING_FACILITIES.${facility}`),
      reset: {
        buildingFacilities: filters.buildingFacilities!.filter(
          (value) => value !== facility,
        ).length
          ? filters.buildingFacilities!.filter((value) => value !== facility)
          : undefined,
      },
    });
  });

  const yesNo = (value: boolean) =>
    value ? t("active-filters.yes") : t("active-filters.no");
  if (filters.hasBalcony !== undefined) {
    chips.push({
      id: "hasBalcony",
      label: named(tL("attribute_title.HAS_BALCONY"), yesNo(filters.hasBalcony)),
      reset: { hasBalcony: undefined },
    });
  }
  if (filters.hasGarden !== undefined) {
    chips.push({
      id: "hasGarden",
      label: named(tL("attribute_title.HAS_GARDEN"), yesNo(filters.hasGarden)),
      reset: { hasGarden: undefined },
    });
  }
  if (filters.floorNumber) {
    chips.push({
      id: "floorNumber",
      label: named(tL("attribute_title.FLOOR_NUMBER"), String(filters.floorNumber)),
      reset: { floorNumber: undefined },
    });
  }
  if (filters.isAvailableNow !== undefined) {
    chips.push({
      id: "isAvailableNow",
      label: filters.isAvailableNow
        ? t("active-filters.available-now")
        : t("active-filters.not-available-now"),
      reset: { isAvailableNow: undefined },
    });
  }
  if (filters.createdAtMin) {
    chips.push({
      id: "createdAtMin",
      label: t("active-filters.listed-since", {
        date: df.dateTime(filters.createdAtMin, {
          day: "numeric",
          month: "short",
        }),
      }),
      reset: { createdAtMin: undefined },
    });
  }
  if (filters.atLeastOneImage) {
    chips.push({
      id: "atLeastOneImage",
      label: t("active-filters.with-photos"),
      reset: { atLeastOneImage: undefined },
    });
  }

  if (!chips.length) {
    return null;
  }

  return (
    <div
      role="region"
      aria-label={t("active-filters.title")}
      className="google-anno-skip"
    >
      <ul
        ref={listRef}
        className="flex flex-row flex-wrap items-center gap-1.5 sm:gap-2"
      >
        {chips.map((chip, index) => (
          <li key={chip.id}>
            <button
              type="button"
              aria-label={t("active-filters.remove", { filter: chip.label })}
              onClick={() => {
                focusIndex.current = index;
                setFilters({ ...filters, ...chip.reset });
              }}
              className="inline-flex h-8 max-w-full items-center gap-1 rounded-full border border-border bg-background pl-3 pr-2 text-xs font-medium text-foreground transition-colors hover:border-zinc-300 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:h-9 sm:gap-1.5 sm:pl-3.5 sm:pr-2.5 sm:text-sm"
            >
              <span className="truncate">{chip.label}</span>
              <X className="size-3.5 shrink-0 text-muted-foreground sm:size-4" />
            </button>
          </li>
        ))}
        <li>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex h-8 items-center rounded-full px-2 text-xs font-semibold text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-9 sm:px-3 sm:text-sm"
          >
            {t("clear-all")}
          </button>
        </li>
      </ul>
    </div>
  );
};

export default SearchActiveFilters;
